import { Table as ToolkitTable } from '@piaas/piaas-ui-toolkit/components';
import { useIntl } from 'react-intl';

import useAppContext from '../../hooks/useAppContext';
import useGlobalLoader from '../../hooks/useGlobalLoader';

import type { DropDownSelectProps } from './utils';

import type {
  AllowSelectedHighlight,
  CreateAction,
  ExportData,
  HeaderActionSlot,
  TableError,
  TableField,
  TableRowData,
} from '@/types/tableTypes';

export interface TableProps {
  isPaginated?: boolean;
  header?: string;
  subHeader?: string;
  fields: TableField[];
  rows?: TableRowData[];
  footerRows?: TableRowData[];
  pageSize?: number;
  setFilter?: (filter: unknown) => void;
  setAdvancedFieldFilter?: (filter: Record<string, string | string[]>) => void;
  totalItems?: number;
  createAction?: CreateAction | null;
  headerActionSlot?: HeaderActionSlot | null;
  exportData?: ExportData | null;
  onPageChange?: (page: number) => void;
  dropDownSelect?: DropDownSelectProps | null;
  search?: boolean;
  advancedSearch?: boolean;
  setItemsPerPage?: (pageSize: number) => void;
  pagination?: boolean;
  isLoading?: boolean;
  error?: TableError | string | null;
  allowSelectedHighlight?: AllowSelectedHighlight | null;
  emptyTableMessageKey?: string;
  styles?: { height?: string | number };
  /**
   * URL query param key for the search term - defaults to 'search'. Use a
   * distinct key per table when more than one can be mounted on the same
   * route, so their search terms don't collide.
   */
  searchParamKey?: string;
}

const Table = ({
  emptyTableMessageKey = 'emptyTable',
  ...props
}: TableProps): React.JSX.Element => {
  const intl = useIntl();
  const { theme } = useAppContext();
  const { loading: isGlobalLoading } = useGlobalLoader();

  return (
    <ToolkitTable
      {...props}
      theme={theme}
      isGlobalLoading={isGlobalLoading}
      formatMessage={(id, values) =>
        intl.formatMessage({ id }, values as Record<string, string | number> | undefined)
      }
      emptyTableMessage={intl.formatMessage({ id: emptyTableMessageKey })}
      paginationLabels={{
        rangeLabel: intl.formatMessage({ id: 'paginationRangeLabel' }),
        rangeAriaLabel: intl.formatMessage({ id: 'paginationRangeAriaLabel' }),
        rowsPerPageAriaLabel: intl.formatMessage({ id: 'paginationRowsPerPageAriaLabel' }),
        rowsPerPageLabel: intl.formatMessage({ id: 'paginationRowsPerPageLabel' }),
        rowsPerPageButtonAriaLabel: intl.formatMessage({
          id: 'paginationRowsPerPageButtonAriaLabel',
        }),
        selectedAriaLabel: intl.formatMessage({ id: 'paginationSelectedAriaLabel' }),
        pageAriaLabel: intl.formatMessage({ id: 'paginationPageAriaLabel' }),
      }}
    />
  );
};

export default Table;
